import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { ApiUrl } from './apiUrl';

@Injectable({
  providedIn: 'root'
})
export class LoyaltyService {

  constructor(private api: ApiService) {}

  // ---- Config ----

  getConfig(storeId: string): Observable<any> {
    return this.api.getRequest(ApiUrl.getLoyaltyConfig(storeId));
  }

  updateConfig(storeId: string, data: any): Observable<any> {
    return this.api.putRequest(ApiUrl.updateLoyaltyConfig(storeId), data);
  }

  // ---- Members ----

  getMembers(storeId: string, params: {
    page?: number;
    limit?: number;
    search?: string;
    tier?: string;
    active?: boolean;
  } = {}): Observable<any> {
    const query = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== '')
      .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
      .join('&');
    const url = ApiUrl.getLoyaltyMembers(storeId) + (query ? `?${query}` : '');
    return this.api.getRequest(url);
  }

  addMember(storeId: string, data: { userId: string; tierKey: string; expiresAt?: string }): Observable<any> {
    return this.api.postRequest(ApiUrl.addLoyaltyMember(storeId), data);
  }

  updateMember(storeId: string, membershipId: string, data: {
    tierKey?: string;
    expiresAt?: string | null;
    isActive?: boolean;
  }): Observable<any> {
    return this.api.putRequest(ApiUrl.updateLoyaltyMember(storeId, membershipId), data);
  }

  deleteMember(storeId: string, membershipId: string): Observable<any> {
    return this.api.deletRequest(ApiUrl.deleteLoyaltyMember(storeId, membershipId));
  }

  // ---- Analytics ----

  getAnalytics(storeId: string, from?: string, to?: string): Observable<any> {
    let url = ApiUrl.getLoyaltyAnalytics(storeId);
    const parts: string[] = [];
    if (from) parts.push(`from=${encodeURIComponent(from)}`);
    if (to) parts.push(`to=${encodeURIComponent(to)}`);
    if (parts.length) url += '?' + parts.join('&');
    return this.api.getRequest(url);
  }

  // ---- Vouchers ----

  getVouchers(storeId: string): Observable<any> {
    return this.api.getRequest(ApiUrl.getLoyaltyVouchers(storeId));
  }

  createVoucher(storeId: string, data: {
    code: string;
    discountType: 'percentage' | 'fixed';
    value: number;
    usageLimit: number;
    expiresAt?: string;
    description?: string;
  }): Observable<any> {
    return this.api.postRequest(ApiUrl.createLoyaltyVoucher(storeId), data);
  }

  updateVoucher(storeId: string, voucherId: string, data: {
    isActive?: boolean;
    expiresAt?: string;
    usageLimit?: number;
  }): Observable<any> {
    return this.api.putRequest(ApiUrl.updateLoyaltyVoucher(storeId, voucherId), data);
  }

  deleteVoucher(storeId: string, voucherId: string): Observable<any> {
    return this.api.deletRequest(ApiUrl.deleteLoyaltyVoucher(storeId, voucherId));
  }

  // ---- User search (for member lookup) ----
  searchUsers(search: string): Observable<any> {
    return this.api.getRequest(`${ApiUrl.getUserSearch}?word=${encodeURIComponent(search)}&limit=20`);
  }

  // ---- Chain Config ----

  getChainConfig(chainId: string): Observable<any> {
    return this.api.getRequest(ApiUrl.getChainLoyaltyConfig(chainId));
  }

  updateChainConfig(chainId: string, data: any): Observable<any> {
    return this.api.putRequest(ApiUrl.updateChainLoyaltyConfig(chainId), data);
  }

  // ---- Chain Members ----

  getChainMembers(chainId: string, params: {
    page?: number;
    limit?: number;
    search?: string;
    tier?: string;
    active?: boolean;
  } = {}): Observable<any> {
    const query = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== '')
      .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
      .join('&');
    const url = ApiUrl.getChainLoyaltyMembers(chainId) + (query ? `?${query}` : '');
    return this.api.getRequest(url);
  }

  addChainMember(chainId: string, data: { userId: string; tierKey: string; expiresAt?: string }): Observable<any> {
    return this.api.postRequest(ApiUrl.addChainLoyaltyMember(chainId), data);
  }

  updateChainMember(chainId: string, membershipId: string, data: {
    tierKey?: string;
    expiresAt?: string | null;
    isActive?: boolean;
  }): Observable<any> {
    return this.api.putRequest(ApiUrl.updateChainLoyaltyMember(chainId, membershipId), data);
  }

  deleteChainMember(chainId: string, membershipId: string): Observable<any> {
    return this.api.deletRequest(ApiUrl.deleteChainLoyaltyMember(chainId, membershipId));
  }

  // ---- Chain Vouchers ----

  getChainVouchers(chainId: string): Observable<any> {
    return this.api.getRequest(ApiUrl.getChainLoyaltyVouchers(chainId));
  }

  createChainVoucher(chainId: string, data: {
    code: string;
    discountType: 'percentage' | 'fixed';
    value: number;
    usageLimit: number;
    expiresAt?: string;
    description?: string;
  }): Observable<any> {
    return this.api.postRequest(ApiUrl.createChainLoyaltyVoucher(chainId), data);
  }

  updateChainVoucher(chainId: string, voucherId: string, data: {
    isActive?: boolean;
    expiresAt?: string | null;
    usageLimit?: number;
  }): Observable<any> {
    return this.api.putRequest(ApiUrl.updateChainLoyaltyVoucher(chainId, voucherId), data);
  }

  deleteChainVoucher(chainId: string, voucherId: string): Observable<any> {
    return this.api.deletRequest(ApiUrl.deleteChainLoyaltyVoucher(chainId, voucherId));
  }
}
